import { salesLink } from './cta';
import { icon } from './icons';
import { SOCIAL_PROFILES, configuredNetworks } from './social';
import { categoryLabel, type InsightCategory } from '../content/insight-categories';
import { SITE_POSTS } from '../content/site-posts';
import type { ExternalPostSource } from '../features/insights/insight-types';
import { getExternalPosts, hasInsightSources, hasLoadedInsights } from '../features/insights/insights-service';
import { escapeHtml, isHttpUrl } from '../lib/html';
import { EXTERNAL_LINK_ATTRS, NEW_TAB_NOTE, routeHref } from '../lib/links';

interface PostCard {
  readonly title: string;
  readonly url: string;
  readonly date: string;
  readonly excerpt: string;
  readonly category: string;
  readonly image?: string;
  readonly source?: ExternalPostSource;
  readonly local: boolean;
}

export function formatPostDate(date: string): string {
  const parsed = new Date(`${date}T12:00:00`);
  return Number.isNaN(parsed.getTime())
    ? escapeHtml(date)
    : parsed.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
}

function insightsEmpty(category: InsightCategory): string {
  const networks = configuredNetworks(['linkedin']); // one channel where space is limited → LinkedIn
  const text =
    category === 'case-studies'
      ? 'Published case studies will appear here once approved by the customer.'
      : 'New insights are on the way. Follow ReTax360 for updates.';
  const links = networks
    .map(
      (k) =>
        `<a class="tlink" href="${SOCIAL_PROFILES[k].url()}" ${EXTERNAL_LINK_ATTRS}>${SOCIAL_PROFILES[k].cta} ${icon('arrow')}${NEW_TAB_NOTE}</a>`,
    )
    .join('');
  return `<div class="empty" role="status"><p>${text}</p><div class="empty-links">${links}${salesLink()}</div></div>`;
}

function postCard(post: PostCard): string {
  const isExternal = !post.local && isHttpUrl(post.url);
  const source = post.source === 'medium' || post.url.includes('medium.com') ? 'Medium' : '';
  return `<li class="card lift post">
      ${post.image ? `<img class="post-img" src="${escapeHtml(post.image)}" alt="" loading="lazy">` : ''}
      <span class="tag">${categoryLabel(post.category) || 'Insights'}</span>
      <h3><a href="${escapeHtml(post.url)}" ${isExternal ? EXTERNAL_LINK_ATTRS : ''}>${escapeHtml(post.title)}${isExternal ? NEW_TAB_NOTE : ''}</a></h3>
      ${post.excerpt ? `<p>${escapeHtml(post.excerpt)}</p>` : ''}
      <p class="fine">${formatPostDate(post.date)}${source ? ' · Read on ' + source : ''}</p></li>`;
}

export function insightsList(category: InsightCategory): string {
  const matches = (postCategory: string): boolean => !category || postCategory === category;
  const local: PostCard[] = SITE_POSTS.filter((p) => matches(p.category)).map((p) => ({
    title: p.title,
    excerpt: p.excerpt,
    date: p.date,
    category: p.category,
    url: routeHref(`/insights/${p.slug}`),
    local: true,
  }));
  const external: PostCard[] = getExternalPosts()
    .filter((p) => matches(p.category))
    .map((p) => ({ ...p, local: false }));
  const list = [...local, ...external];
  if (!list.length) {
    return hasLoadedInsights() || !hasInsightSources()
      ? insightsEmpty(category)
      : '<div class="empty" role="status"><p>Loading articles…</p></div>';
  }
  return `<ul class="grid3 posts">${list.map(postCard).join('')}</ul>`;
}
